/**
 * EQ-5D-5L Cuestionario de Calidad de Vida
 * 
 * NOTA LEGAL: El EQ-5D-5L es un instrumento del EuroQol Group.
 * Los textos oficiales de las preguntas requieren licencia.
 * Esta implementación solo define la ESTRUCTURA, los textos
 * deben configurarse desde admin cuando se tenga autorización.
 */

// Configuración con textos propios (versión simple para pacientes)
export const EQ5D5L_CONFIG = {
  // Si está habilitada la escala VAS adicional
  enableVAS: true,
  
  // País para el cálculo del index value (null = no calcular)
  valueSetCountry: null, // 'ES', 'UK', etc.
  
  // Días para advertir de cuestionario reciente
  recentDaysWarning: 14,
  
  // Textos configurables
  texts: {
    title: "CUESTIONARIO: CALIDAD DE VIDA",
    subtitle: "(versión simple para pacientes)",
    instructions: "Marca la opción que mejor describe cómo te encuentras HOY.",
    vasInstructions: "Indica tu número de salud global hoy",
    vasLabel: "(Opcional) Salud global hoy (0–100)",
    vasDescription: "0 = la peor salud imaginable | 100 = la mejor salud imaginable",
    
    // Dimensiones con textos propios
    dimensions: {
      mobility: {
        id: "mobility",
        title: "1) Movilidad (caminar y moverse)",
        question: "",
        levels: [
          { value: 1, label: "Me muevo sin dificultad" },
          { value: 2, label: "Tengo una dificultad leve para moverme" },
          { value: 3, label: "Tengo una dificultad moderada para moverme" },
          { value: 4, label: "Tengo mucha dificultad para moverme" },
          { value: 5, label: "Apenas puedo moverme / no puedo moverme sin ayuda" },
        ]
      },
      selfcare: {
        id: "selfcare",
        title: "2) Autocuidado (aseo y vestirse)",
        question: "",
        levels: [
          { value: 1, label: "Me cuido (aseo/vestirme) sin dificultad" },
          { value: 2, label: "Tengo una dificultad leve para cuidarme" },
          { value: 3, label: "Tengo una dificultad moderada para cuidarme" },
          { value: 4, label: "Tengo mucha dificultad para cuidarme" },
          { value: 5, label: "No puedo cuidarme sin ayuda" },
        ]
      },
      usual_activities: {
        id: "usual_activities",
        title: "3) Actividades diarias (trabajo/estudios/casa/ocio)",
        question: "",
        levels: [
          { value: 1, label: "Hago mis actividades diarias con normalidad" },
          { value: 2, label: "Tengo una dificultad leve para hacerlas" },
          { value: 3, label: "Tengo una dificultad moderada para hacerlas" },
          { value: 4, label: "Tengo mucha dificultad para hacerlas" },
          { value: 5, label: "No puedo hacer mis actividades diarias" },
        ]
      },
      pain_discomfort: {
        id: "pain_discomfort",
        title: "4) Dolor o malestar",
        question: "",
        levels: [
          { value: 1, label: "No tengo dolor ni malestar" },
          { value: 2, label: "Tengo dolor/malestar leve" },
          { value: 3, label: "Tengo dolor/malestar moderado" },
          { value: 4, label: "Tengo dolor/malestar intenso" },
          { value: 5, label: "Tengo dolor/malestar muy intenso" },
        ]
      },
      anxiety_depression: {
        id: "anxiety_depression",
        title: "5) Estado emocional (ansiedad o tristeza)",
        question: "",
        levels: [
          { value: 1, label: "Me siento tranquilo/a y estable" },
          { value: 2, label: "Me siento algo preocupado/a o triste" },
          { value: 3, label: "Me siento moderadamente ansioso/a o triste" },
          { value: 4, label: "Me siento muy ansioso/a o triste" },
          { value: 5, label: "Me siento extremadamente ansioso/a o triste" },
        ]
      }
    }
  }
};

// Schema para el sistema de cuestionarios
export const EQ5D5L_SCHEMA = {
  id: 'eq5d5l',
  name: 'Calidad de vida (EQ-5D-5L)',
  fullName: 'EuroQol 5 Dimensiones 5 Niveles',
  description: 'Cuestionario estandarizado de calidad de vida relacionada con la salud. Evalúa 5 dimensiones del estado de salud.',
  estimatedTime: '2-3 minutos',
  category: 'calidad_vida',
  version: '1.0',
  requiresLicense: true,
  licenseNote: 'Este instrumento pertenece al EuroQol Group. Uso sujeto a licencia.',
  isEQ5D: true,
  scoring: {
    type: 'health_state',
    method: 'concatenation',
    dimensions: 5,
    levels: 5
  },
  sections: [
    {
      id: 'eq5d_dimensions',
      title: 'Estado de Salud',
      questions: [
        {
          id: 'mobility_level',
          dimensionId: 'mobility',
          type: 'eq5d_dimension',
          required: true,
          min: 1,
          max: 5,
        },
        {
          id: 'selfcare_level',
          dimensionId: 'selfcare',
          type: 'eq5d_dimension',
          required: true,
          min: 1,
          max: 5,
        },
        {
          id: 'usual_activities_level',
          dimensionId: 'usual_activities',
          type: 'eq5d_dimension',
          required: true,
          min: 1,
          max: 5,
        },
        {
          id: 'pain_discomfort_level',
          dimensionId: 'pain_discomfort',
          type: 'eq5d_dimension',
          required: true,
          min: 1,
          max: 5,
        },
        {
          id: 'anxiety_depression_level',
          dimensionId: 'anxiety_depression',
          type: 'eq5d_dimension',
          required: true,
          min: 1,
          max: 5,
        },
      ],
    },
    {
      id: 'eq5d_vas',
      title: 'Autovaloración de Salud',
      optional: true,
      questions: [
        {
          id: 'eq_vas_0_100',
          type: 'vas_scale',
          required: false,
          min: 0,
          max: 100,
          leftLabel: 'Peor salud imaginable',
          rightLabel: 'Mejor salud imaginable',
        },
      ],
    },
  ],
};

// Función para generar el health state code
export const generateHealthStateCode = (answers) => {
  const mobility = answers.mobility_level || 0;
  const selfcare = answers.selfcare_level || 0;
  const usualActivities = answers.usual_activities_level || 0;
  const painDiscomfort = answers.pain_discomfort_level || 0;
  const anxietyDepression = answers.anxiety_depression_level || 0;
  
  if (!mobility || !selfcare || !usualActivities || !painDiscomfort || !anxietyDepression) {
    return null;
  }
  
  return `${mobility}${selfcare}${usualActivities}${painDiscomfort}${anxietyDepression}`;
};

// Validar respuestas
export const validateEQ5D5LAnswers = (answers) => {
  const errors = [];
  const dimensions = ['mobility_level', 'selfcare_level', 'usual_activities_level', 'pain_discomfort_level', 'anxiety_depression_level'];
  
  dimensions.forEach(dim => {
    const value = answers[dim];
    if (value === undefined || value === null) {
      errors.push(`${dim} es obligatorio`);
    } else if (value < 1 || value > 5) {
      errors.push(`${dim} debe estar entre 1 y 5`);
    }
  });
  
  // VAS es opcional, pero si existe debe ser 0-100
  if (answers.eq_vas_0_100 !== undefined && answers.eq_vas_0_100 !== null) {
    if (answers.eq_vas_0_100 < 0 || answers.eq_vas_0_100 > 100) {
      errors.push('EQ-VAS debe estar entre 0 y 100');
    }
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

// Calcular score EQ-5D-5L
export const calculateEQ5D5LScore = (answers) => {
  const validation = validateEQ5D5LAnswers(answers);
  if (!validation.isValid) {
    return { error: validation.errors };
  }
  
  const healthStateCode = generateHealthStateCode(answers);
  
  // Calcular un "summary index" simple (suma de niveles, menor = mejor)
  // NOTA: El index_value oficial requiere tablas de valores por país (licencia)
  const levelSum = 
    answers.mobility_level + 
    answers.selfcare_level + 
    answers.usual_activities_level + 
    answers.pain_discomfort_level + 
    answers.anxiety_depression_level;
  
  // Puntuación simple normalizada (5 = perfecto, 25 = peor)
  // Convertir a escala 0-100 donde 100 es mejor
  const simpleScore = Math.round(((25 - levelSum) / 20) * 100);
  
  return {
    health_state_code: healthStateCode,
    level_sum: levelSum,
    simple_score: simpleScore, // 0-100, mayor = mejor
    eq_vas: answers.eq_vas_0_100 ?? null,
    dimensions: {
      mobility: answers.mobility_level,
      selfcare: answers.selfcare_level,
      usual_activities: answers.usual_activities_level,
      pain_discomfort: answers.pain_discomfort_level,
      anxiety_depression: answers.anxiety_depression_level,
    },
    // index_value: null, // Solo calcular si hay tabla de valores autorizada
    interpretation: getHealthStateInterpretation(healthStateCode),
  };
};

// Interpretación básica del estado de salud
const getHealthStateInterpretation = (code) => {
  if (!code) return 'Incompleto';
  
  const sum = code.split('').reduce((a, b) => a + parseInt(b), 0);
  
  if (code === '11111') return 'Estado de salud perfecto';
  if (sum <= 7) return 'Estado de salud muy bueno';
  if (sum <= 10) return 'Estado de salud bueno';
  if (sum <= 15) return 'Estado de salud moderado';
  if (sum <= 20) return 'Estado de salud con problemas significativos';
  return 'Estado de salud con problemas severos';
};

export default EQ5D5L_SCHEMA;
